import React from "react";
import {
  Avatar,
  Box,
  Grid,
  LinearProgress,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import Rating from "@material-ui/lab/Rating";
import Skeleton from "@material-ui/lab/Skeleton";
import moment from "moment";

const useStyles = makeStyles(theme => ({
  grid: {
    padding: 8,
  },
  card: {
    height: "100%",
    padding: 18,
    borderRadius: 18,
    background: "#fff",
    border: "1px solid rgba(227,6,19,0.10)",
    boxShadow: "0 10px 26px rgba(15,15,15,0.07)",
  },
  top: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 14,
  },
  avatar: {
    width: 52,
    height: 52,
    fontWeight: 900,
    background: "linear-gradient(135deg,#e30613,#111)",
    color: "#fff",
  },
  name: {
    fontWeight: 900,
    color: "#111",
    lineHeight: 1.1,
  },
  status: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    marginTop: 5,
    padding: "4px 9px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 900,
  },
  online: {
    background: "#dcfce7",
    color: "#166534",
  },
  offline: {
    background: "#fee2e2",
    color: "#991b1b",
  },
  metrics: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginTop: 12,
  },
  metric: {
    padding: 12,
    borderRadius: 14,
    background: "#f8fafc",
  },
  metricLabel: {
    fontSize: 11,
    color: "#64748b",
    fontWeight: 800,
  },
  metricValue: {
    display: "block",
    marginTop: 4,
    fontSize: 18,
    color: "#111",
    fontWeight: 950,
  },
  alert: {
    marginTop: 14,
    padding: "10px 12px",
    borderRadius: 14,
    fontWeight: 900,
    fontSize: 12,
  },
  ok: {
    background: "#dcfce7",
    color: "#166534",
  },
  warn: {
    background: "#fef3c7",
    color: "#92400e",
  },
  danger: {
    background: "#fee2e2",
    color: "#991b1b",
  },
  progress: {
    marginTop: 12,
    height: 8,
    borderRadius: 999,
    background: "#fee2e2",
  },
}));

function formatTime(minutes) {
  return moment().startOf("day").add(Number(minutes || 0), "minutes").format("HH[h] mm[m]");
}

function avatarUrl(a) {
  return a.profilePicUrl || a.profilePic || a.picture || a.avatar || "";
}

function riskInfo(a) {
  const tickets = Number(a.tickets || 0);
  const wait = Number(a.avgWaitTime || 0);

  if (!a.online) return { text: "Crítico: atendente offline", cls: "danger", score: 25 };
  if (tickets >= 15) return { text: "Crítico: fila muito alta", cls: "danger", score: 90 };
  if (tickets >= 8 || wait >= 30) return { text: "Atenção: acompanhar operação", cls: "warn", score: 65 };
  return { text: "Operação saudável", cls: "ok", score: 35 };
}

export default function TableAttendantsStatus(props) {
  const { loading, attendants = [] } = props;
  const classes = useStyles();

  if (loading) {
    return <Skeleton variant="rect" height={180} />;
  }

  if (!attendants.length) {
    return (
      <Box p={2}>
        <Typography>Nenhum atendente encontrado no período.</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2} className={classes.grid}>
      {attendants.map((a, index) => {
        const risk = riskInfo(a);
        const initial = String(a.name || "?").charAt(0).toUpperCase();

        return (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Box className={classes.card}>
              <Box className={classes.top}>
                <Avatar src={avatarUrl(a)} className={classes.avatar}>
                  {initial}
                </Avatar>

                <Box flex={1}>
                  <Typography className={classes.name}>{a.name}</Typography>

                  <Tooltip title={a.online ? "Online" : "Offline"}>
                    <span className={`${classes.status} ${a.online ? classes.online : classes.offline}`}>
                      {a.online ? <CheckCircleIcon style={{ fontSize: 14 }} /> : <ErrorIcon style={{ fontSize: 14 }} />}
                      {a.online ? "Online" : "Offline"}
                    </span>
                  </Tooltip>
                </Box>
              </Box>

              <Rating value={Number(a.rating || 0)} max={3} readOnly size="small" />

              <Box className={classes.metrics}>
                <Box className={classes.metric}>
                  <span className={classes.metricLabel}>Tickets</span>
                  <strong className={classes.metricValue}>{a.tickets || 0}</strong>
                </Box>

                <Box className={classes.metric}>
                  <span className={classes.metricLabel}>Avaliações</span>
                  <strong className={classes.metricValue}>{a.countRating || 0}</strong>
                </Box>

                <Box className={classes.metric}>
                  <span className={classes.metricLabel}>Espera média</span>
                  <strong className={classes.metricValue}>{formatTime(a.avgWaitTime)}</strong>
                </Box>

                <Box className={classes.metric}>
                  <span className={classes.metricLabel}>Atendimento médio</span>
                  <strong className={classes.metricValue}>{formatTime(a.avgSupportTime)}</strong>
                </Box>
              </Box>

              <LinearProgress
                variant="determinate"
                value={risk.score}
                className={classes.progress}
              />

              <Box className={`${classes.alert} ${classes[risk.cls]}`}>
                {risk.text}
              </Box>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
}
